// A33 — Sandbox tab. The default tab in the workspace's main column.
// Renders a live noVNC view of the Daytona sandbox so the user watches
// the working agent operate in real time.
//
// 2026-04-27 — the iframe loads through Waytide's Custom Preview Proxy
// (backend module sandbox_proxy_routes), which injects Daytona's
// X-Daytona-Skip-Preview-Warning header on each request so the warning
// interstitial never reaches the user. Daytona officially documents
// this proxy pattern as the canonical iframe-side bypass; iframes
// can't add custom headers, only a server-side proxy can. The proxy
// also bridges the noVNC WebSocket so the framebuffer streams.
//
// Auth is an HMAC-signed proxy token (1h TTL) minted via
// mintSandboxProxyToken after JWT-authed workspace membership check.
// We re-mint every 50 minutes so a long-watched sandbox session
// doesn't drop when the token expires.
//
// Sandbox interactivity is universal as of 2026-04-27. The retired
// free-read-only / premium-take-control split is intentionally gone:
// every tier gets the same iframe sandbox and allow attributes.

import { useEffect, useRef, useState } from "react";
import {
  buildSandboxProxyUrl,
  getWorkspaceSandboxDetails,
  mintSandboxProxyToken,
  setSandboxDesktopColor,
  setSandboxResolution,
  type Entitlements,
  type SandboxDetailsResponse,
} from "../../api/client";

interface Props {
  workspaceId: number;
  /** Kept for prop-shape compat with WorkspaceRoomPage even though the
   * SandboxTab no longer reads it. 2026-04-27 canon: sandbox
   * interactivity is universal across tiers. */
  entitlements: Entitlements | null;
}

// Two cadences — fast while the sandbox is warming up so the
// iframe mounts within ~1 s of vncReady flipping true, then slower
// once the live view is up so the polling cost stays low.
const POLL_INTERVAL_MS_WARMING = 1000;
const POLL_INTERVAL_MS_LIVE = 8000;
// Backend mints proxy tokens with a 1h TTL. Re-mint with a 10-minute
// safety margin so the iframe never sees an expired token mid-session.
const PROXY_TOKEN_REFRESH_MS = 50 * 60 * 1000;

export function SandboxTab({ workspaceId, entitlements: _entitlements }: Props) {
  const [details, setDetails] = useState<SandboxDetailsResponse | null>(null);
  const [proxyUrl, setProxyUrl] = useState<string | null>(null);

  // 2026-04-27 — interactivity is universal AND the toggle pill is
  // gone, so the iframe always carries the wider sandbox+allow attrs
  // unconditionally. The previous per-workspace localStorage toggle
  // (`sandbox_interactive_${workspaceId}`) is no longer read.

  // Two-cadence polling — 1 s while the sandbox is still warming
  // up (no vncReady yet), then 8 s once it's live and the iframe is
  // mounted. The fast cadence shaves perceived latency on first
  // workspace load (8 s → ~1 s between "stack came up server-side"
  // and "iframe mounts client-side").
  const isWarming = details === null || details.vncReady !== true;
  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    async function fetchOnce() {
      try {
        const next = await getWorkspaceSandboxDetails(workspaceId);
        if (!cancelled) setDetails(next);
      } catch {
        // Silent — the popout pattern already swallows transient
        // Daytona errors; the placeholder is the fallback UX.
      }
    }
    fetchOnce();
    const interval = isWarming ? POLL_INTERVAL_MS_WARMING : POLL_INTERVAL_MS_LIVE;
    const timer = window.setInterval(fetchOnce, interval);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [workspaceId, isWarming]);

  // Mint a proxy token whenever the sandbox first reports vnc_ready,
  // then refresh on a 50-minute cadence so the iframe never observes
  // an expired token. We gate on details?.vnc_ready (not just
  // previewUrl) because the previewUrl appears as soon as Daytona has
  // a sandbox, but noVNC's port 6080 doesn't bind until the
  // computer-use stack finishes starting up — and an iframe that
  // mounts against a 502'd URL doesn't auto-recover, so users would
  // see a permanent broken state until they refreshed. Backend probes
  // the upstream and sets vnc_ready only when port 6080 returns 2xx.
  const sandboxLive = details?.vncReady === true;
  useEffect(() => {
    if (!workspaceId || !sandboxLive) {
      setProxyUrl(null);
      return;
    }
    let cancelled = false;
    async function mint() {
      try {
        const m = await mintSandboxProxyToken(workspaceId);
        if (!cancelled) setProxyUrl(buildSandboxProxyUrl(m.token, m.port));
      } catch {
        // Silent — the placeholder UX is the fallback.
      }
    }
    void mint();
    const timer = window.setInterval(() => void mint(), PROXY_TOKEN_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [workspaceId, sandboxLive]);

  // Match the sandbox's Xvfb resolution to this iframe's actual
  // pixel dimensions. ResizeObserver fires on every container size
  // change (window resize, drawer toggle, dev console open, etc.);
  // we debounce 200ms then POST the new dims. Backend xrandrs into
  // place — no Xvfb restart, no flicker, sub-second on the wire.
  // The xrandr path requires patch_xvfb_for_dynamic_resize having
  // run on this sandbox first (it has, via auto_spawn / wake) so
  // Xvfb's RANDR extension is enabled.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastSentRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const resizeTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!workspaceId || !sandboxLive) return;
    const el = containerRef.current;
    if (!el) return;
    const push = (width: number, height: number) => {
      // Round to nearest 8 (matches backend clamp+round) so a few-px
      // ResizeObserver jitter doesn't trip a redundant POST.
      const w = Math.round(width / 8) * 8;
      const h = Math.round(height / 8) * 8;
      // Sanity floor — drop nonsense dims (e.g. 0 from a transient
      // unmount) but keep narrow real ones. The previous 640x480
      // gate was the bug behind "It's fine at sizes above this":
      // a portrait drawer-open container at 544×1344 fell BELOW
      // 640 width and the POST was silently dropped. Any container
      // wide enough to be meaningfully VNC-readable (≥256 px) is
      // worth resizing for. Backend mirrors the floor.
      if (w < 256 || h < 256) return;
      if (lastSentRef.current.w === w && lastSentRef.current.h === h) return;
      lastSentRef.current = { w, h };
      console.debug("[sandbox] resize push", w, h);
      void setSandboxResolution(workspaceId, w, h).catch((err) => {
        console.warn("[sandbox] resize POST failed", w, h, err);
      });
    };
    const schedule = (width: number, height: number) => {
      if (resizeTimerRef.current !== null) {
        window.clearTimeout(resizeTimerRef.current);
      }
      resizeTimerRef.current = window.setTimeout(() => {
        resizeTimerRef.current = null;
        push(width, height);
      }, 200);
    };
    // Fire once immediately for current size.
    schedule(el.clientWidth, el.clientHeight);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        schedule(cr.width, cr.height);
      }
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (resizeTimerRef.current !== null) {
        window.clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
  }, [workspaceId, sandboxLive]);

  // Desktop color sync — XFCE renders a default wallpaper image
  // that (a) doesn't fit dynamic resolutions and (b) clashes with
  // the surrounding Waytide theme. We can't make XFCE's root window
  // transparent (X requires a backdrop), so the next-best thing is
  // matching the iframe-container color exactly. The iframe
  // container's effective bg is `bg-surface` → `--wt-surface` →
  // `def.bg` per ThemeContext. We read the SAME var so the chrome
  // and the desktop are guaranteed identical.
  //
  // Color-space conversion: themes may set bg via hex, color-mix(),
  // oklab(), lch(), or named colors — any CSS <color>. Hand-rolling
  // a parser for all of those is brittle; we instead let the
  // browser canonicalise by painting the value into a 1×1 canvas
  // and reading the sRGB pixel back. xfconf-query expects sRGB
  // floats so this is the right colour space for the wire too.
  useEffect(() => {
    if (!workspaceId || !sandboxLive) return;
    let cancelled = false;
    const sync = () => {
      const surface = getComputedStyle(document.documentElement)
        .getPropertyValue("--wt-surface")
        .trim();
      if (!surface) return;
      const rgb = cssColorToSrgb(surface);
      if (!rgb) {
        console.warn("[sandbox] desktop-color: failed to parse", surface);
        return;
      }
      void setSandboxDesktopColor(workspaceId, rgb.r, rgb.g, rgb.b).catch((err) => {
        if (!cancelled) console.warn("[sandbox] desktop-color POST failed", err);
      });
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style", "class"],
    });
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [workspaceId, sandboxLive]);

  // Layout: iframe fills the area below the TabBar (top-14) all the
  // way to the bottom edge. 2026-04-27 — floating chrome (status
  // pill, Take-Control toggle) removed; the iframe owns the full
  // visible area without competition. Daytona's own content owns
  // the inner scroll if the page is taller than the iframe.
  return (
    <div
      ref={containerRef}
      className="absolute bottom-0 left-0 right-0 top-14 overflow-hidden"
    >
      {proxyUrl ? (
        <iframe
          key={proxyUrl}
          src={proxyUrl}
          title="Sandbox display"
          className="absolute inset-0 h-full w-full border-0 bg-surface [cursor:none]"
          // 2026-04-27 — sandbox interactivity is universal, so the
          // iframe always carries the wider sandbox+allow attrs.
          // allow-scripts + allow-same-origin let noVNC's JS run and
          // open WebSockets back through the proxy. The
          // `allow-pointer-lock` sandbox flag IS the pointer-lock
          // mechanism; `pointer-lock` is NOT a Permissions Policy
          // feature (Chromium emits "Unrecognized feature" if you put
          // it in `allow=`), so the policy attr only carries the real
          // policy directives.
          sandbox="allow-scripts allow-same-origin allow-pointer-lock allow-forms allow-popups"
          allow="clipboard-read; clipboard-write"
          data-testid="sandbox-iframe"
        />
      ) : (
        <SandboxGhost details={details} />
      )}

      {/* 2026-04-27 — floating status + Take-Control pills removed
          from the sandbox surface. Status surfaces in the rail /
          /sandbox/details popout already; interactivity is universal
          so the toggle is redundant. SandboxTab now gives the iframe
          the entire visible area without chrome competing for it. */}
    </div>
  );
}

// SandboxGhost — placeholder shown while the sandbox is
// provisioning / waking, before vncReady flips true.
//
// Pattern (industry research, 2026-04-28): combine a faux XFCE
// desktop chrome (top panel + dock at the iframe aspect ratio) with
// a phased status panel tied to actual backend state. Reads as
// "a Linux desktop is materialising" not "generic content loading".
// References: GitHub Codespaces phased status timeline, Vercel
// Sandbox shimmer-frame, Hyperbeam fake-browser-chrome,
// CodeSandbox/StackBlitz boot-step list, noVNC's own RFB status
// surfacing. Aspect-ratio match means zero layout shift on iframe
// mount — the chrome shape already fills the slot.
//
// Phases:
//   1. Provisioning sandbox    — Daytona is creating the VM
//   2. Starting display stack  — Xvfb / xfce4-session / x11vnc
//   3. Connecting noVNC        — websockify handshake / framebuffer
// Phase advances on the actual `details.state` + `previewUrl`
// signals; flips spinner→check as each lands. Each row is keyed to
// a real condition so the user sees the system's actual progress
// rather than a synthetic timer.
function SandboxGhost({ details }: { details: SandboxDetailsResponse | null }) {
  // Always render the phased panel — sleeping / asleep / fetching
  // all collapse onto the same UX of "the sandbox is becoming
  // ready". The previous "Sandbox asleep" copy variant was a
  // dead-end placeholder that read as an error rather than a load
  // step; user explicitly asked for "always loading modal". States
  // that don't yet have a preview URL just leave the later phases
  // pending — visually identical to a cold provision.
  const haveState =
    details !== null && (details.state === "active" || details.state === "warming");
  const havePreview = !!details?.previewUrl;
  const phases: Array<{ label: string; done: boolean; active: boolean }> = [
    {
      label: "Provisioning sandbox",
      done: haveState,
      active: !haveState,
    },
    {
      label: "Starting display stack (Xvfb · XFCE)",
      done: havePreview,
      active: haveState && !havePreview,
    },
    {
      label: "Connecting noVNC display",
      done: false, // becomes true exactly when vncReady flips, at which point this component unmounts
      active: havePreview,
    },
  ];
  return (
    <div
      className="absolute inset-0 flex flex-col overflow-hidden bg-surface"
      data-testid="sandbox-ghost"
    >
      {/* Faux XFCE top panel — Applications menu dot at left, clock
          stub at right. Same proportions as the real XFCE panel so
          there's no layout jolt when the iframe mounts. */}
      <div className="flex h-7 shrink-0 items-center justify-between border-b border-edge bg-surface-raised px-3">
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-edge" />
          <div className="h-2 w-16 animate-pulse rounded-sm bg-edge" />
        </div>
        <div className="h-2 w-12 animate-pulse rounded-sm bg-edge" />
      </div>
      {/* Wallpaper area — a slow horizontal shimmer mask sweeping a
          gradient over the surface. Reads as "pixels arriving" not
          "blank canvas". CSS-only, GPU-accelerated. */}
      <div className="relative flex-1 overflow-hidden">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "linear-gradient(135deg, var(--wt-surface) 0%, var(--wt-surface-raised) 50%, var(--wt-surface) 100%)",
            backgroundSize: "200% 200%",
            animation: "wt-ghost-sweep 6s linear infinite",
          }}
        />
        {/* Centered phase panel — translucent card with three rows
            keyed to real backend state. The user sees the system
            progressing, not a meaningless spinner. */}
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <div className="rounded-panel border border-edge bg-surface-glass px-6 py-5 backdrop-blur">
            <p className="mb-3 text-xs font-semibold text-primary">
              Materialising desktop
            </p>
            <ul className="flex flex-col gap-2">
              {phases.map((p) => (
                <li key={p.label} className="flex items-center gap-2.5 text-xs">
                  <PhaseIcon done={p.done} active={p.active} />
                  <span className={p.done || p.active ? "text-primary" : "text-faint"}>
                    {p.label}
                  </span>
                </li>
              ))}
            </ul>
            {details?.errorReason && (
              <p className="mt-3 max-w-xs text-xs text-warn">{details.errorReason}</p>
            )}
          </div>
        </div>
      </div>
      {/* Faux XFCE dock — five app-icon squares centred at the
          bottom. The real xfce4-panel dock is 4-6 icons in this
          band, so the iframe-mount swap stays visually flush. */}
      <div className="flex h-12 shrink-0 items-center justify-center gap-1.5 border-t border-edge bg-surface-raised">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="h-7 w-7 animate-pulse rounded-md bg-edge"
            style={{ animationDelay: `${i * 120}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

function PhaseIcon({ done, active }: { done: boolean; active: boolean }) {
  if (done) {
    return (
      <svg
        viewBox="0 0 16 16"
        className="h-3.5 w-3.5 shrink-0 text-success"
        aria-hidden
      >
        <path
          fill="currentColor"
          d="M6.5 11.293 3.207 8 4.621 6.586 6.5 8.464l4.879-4.878 1.414 1.414z"
        />
      </svg>
    );
  }
  if (active) {
    return (
      <svg
        viewBox="0 0 16 16"
        className="h-3.5 w-3.5 shrink-0 animate-spin text-accent"
        aria-hidden
      >
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.25" />
        <path
          d="M8 2 a6 6 0 0 1 6 6"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  return <div className="h-3.5 w-3.5 shrink-0 rounded-full border border-edge" />;
}

// Bulletproof CSS-color → sRGB conversion. Accepts ANY value the
// browser's CSS engine accepts: hex (#rgb / #rrggbb / #rrggbbaa),
// rgb()/rgba(), hsl()/hsla(), hwb(), lab(), lch(), oklab(), oklch(),
// color() with any colour space, color-mix(), named colours, etc.
// Implementation: paint the value into a 1×1 canvas and read the
// pixel back. The browser handles all parsing + colour-space
// conversion in canvas's native sRGB context. Returns null only
// when the value is genuinely invalid (canvas leaves fillStyle as
// the previous black, which we detect by sentinel-init).
//
// Why not hand-roll a parser: themes use color-mix(in srgb, ...)
// today and may use oklab() / lch() tomorrow; a string-matcher
// would silently fall back to the wrong colour for every new CSS
// colour-space the design system adopts. Canvas is the canonical
// "let the browser convert anything to sRGB" primitive.
function cssColorToSrgb(value: string): { r: number; g: number; b: number } | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  // Paint a sentinel first; if fillStyle assignment is rejected
  // (invalid value), the canvas keeps the sentinel and we can
  // tell — fillStyle silently no-ops on an invalid value rather
  // than throwing.
  ctx.fillStyle = "#ff00ff";
  ctx.fillRect(0, 0, 1, 1);
  ctx.fillStyle = value;
  // If the assignment was rejected, fillStyle stays at #ff00ff
  // (browser-canonicalised to "#ff00ff" or "rgb(255, 0, 255)").
  const accepted = ctx.fillStyle !== "#ff00ff" && ctx.fillStyle !== "rgb(255, 0, 255)";
  if (!accepted) return null;
  ctx.fillRect(0, 0, 1, 1);
  const data = ctx.getImageData(0, 0, 1, 1).data;
  return { r: data[0], g: data[1], b: data[2] };
}
